import React from 'react';
import { Typography, Popper, Button, Paper, Link, Drawer, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import {
  usePopupState,
  bindHover,
  bindPopper,
} from 'material-ui-popup-state/hooks';
import { 
  Info, 
  Build, 
  DocumentScanner, 
  DirectionsCar, 
  MoreHoriz,
  Chat, 
  Lock, 
  NotificationsActive,
  QuestionAnswer,
  NewReleases,
  ContactSupport,
  ArrowForward,
  HelpOutline,
  Translate,
  School,
  Business,
  Menu as MenuIcon,
  Close as CloseIcon,
  Update as UpdateIcon,
  Forum,
  Star,
  Android
} from '@mui/icons-material';
import { Box, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery } from '@mui/material';

const options = [
  { 
    label: 'Subjects', 
    content: [
      { text: 'Languages on Uben', icon: <Translate />, description: 'Arabic, Chinese, English, French, German, Hebrew, Japanese, Korean, Russian, Spanish, and more!' },
      { text: 'Sciences', icon: <School />, description: 'Anatomy, Biology, Chemistry, Geography, History, Law, Math, Music, Pathology, Physics, and more!' },
      { text: 'Art & Trivia', icon: <Business />, description: 'Custom decks for any subject including coding, DSA, and more!' }
    ]
  },
  { 
    label: 'Pricing', 
    content: [
      { text: 'Free Plan', icon: <School />, description: 'Learn with our feature-rich free plan.' },
      { text: 'Go Pro', icon: <Star />, description: 'Unlock premium community decks, curated resources, and a Pro badge.', href: '/pricing' },
      { text: 'Support on Ko-fi', icon: <NewReleases />, description: 'Support Uben\'s development on Ko-fi.', href: 'https://ko-fi.com/kurizu' },
    ]
  },
  { 
    label: 'Updates', 
    content: [
      { text: 'Project Status', icon: <UpdateIcon />, description: 'See the latest updates, roadmap, and to-do list for Uben.', href: '/updates' },
    ]
  },
  { 
    label: 'Features', 
    content: [
      { text: 'Universal Learning', icon: <NewReleases />, description: 'Learn any subject with our comprehensive platform. Anki import available for 10 supported languages.' },
      { text: 'Android App (.apk)', icon: <Android />, description: 'Download the official Android app APK directly.', href: '/uben.apk' },
      { text: 'Smart Flashcards', icon: <QuestionAnswer />, description: 'Adaptive flashcards that adjust to your learning pace.' },
      { text: 'Progress Tracking', icon: <NotificationsActive />, description: 'Track your learning progress with detailed statistics.', scrollTo: 'progress_tracking' },
      { text: 'Custom Deck Creation', icon: <Build />, description: 'Create your own personalized decks with our easy-to-use interface. Add cards with custom fields to match your learning needs.' },
      { text: 'Anki Integration', icon: <DocumentScanner />, description: 'Import your existing Anki decks for Arabic, Chinese, English, French, German, Hebrew, Japanese, Korean, Russian, Spanish.', scrollTo: 'import' },
    ]
  },
  { 
    label: 'Community', 
    content: [
      { text: 'Forums', icon: <Forum />, description: 'Join discussions in our Uben and Kindle community forums.', scrollTo: 'forums' },
      { text: 'Join Discord Server', icon: <Chat />, description: 'Connect with other learners and get help from the community.', href: 'https://discord.gg/JtcrpG7ECA' },
      { text: 'Contact Support', icon: <ContactSupport />, description: 'Get help with your account or report any issues.', href: 'mailto:uben.learn@gmail.com' },
    ]
  }
];

const DropdownOption = ({ option, isMobile = false, onClose }) => {
  const { label, content } = option;
  const popupState = usePopupState({
    variant: 'popper',
    popupId: `popper-${label}`
  });

  const [mobileDialogOpen, setMobileDialogOpen] = React.useState(false);

  const handleMobileCardClick = () => {
    if (isMobile) {
      setMobileDialogOpen(true);
    }
  };

  const handleMobileDialogClose = () => {
    setMobileDialogOpen(false);
  };

  const handleScrollTo = (scrollToId) => {
    const element = document.getElementById(scrollToId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (onClose) onClose();
  };

  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");

  if (isMobile) {
    return (
      <>
        <Box 
          onClick={handleMobileCardClick}
          sx={{ 
            borderRadius: '8px',
            backgroundColor: 'rgba(122, 177, 248, 0.9)', // Using your blue theme
            border: '1px solid rgba(86,118,191,1)',
            padding: '0.4rem',
            transition: 'all 0.2s ease-in-out',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'rgba(86,118,191,1)', // Darker blue on hover
              color: 'white',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              cursor: 'default',
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '50px',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: '600', 
              color: 'white', // White text on blue background
              fontSize: '0.75rem',
            }}
          >
            {label}
          </Typography>
        </Box>

        <Dialog
          open={mobileDialogOpen}
          onClose={handleMobileDialogClose}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '12px',
              margin: '1rem',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              backgroundColor: 'rgba(86,118,191,1)', // Using your blue theme
              color: 'white',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              fontWeight: 'bold',
              fontSize: '1.25rem',
            }}
          >
            {label}
            <IconButton onClick={handleMobileDialogClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <List sx={{ p: 0 }}>
              {content.map((item, index) => (
                <ListItem 
                  key={index} 
                  component={item.href || item.scrollTo ? Link : 'div'}
                  href={item.href ? item.href : undefined}
                  target={item.href && (item.href.startsWith('/') || item.href.startsWith('#')) ? '_self' : '_blank'}
                  onClick={item.scrollTo ? () => { handleScrollTo(item.scrollTo); handleMobileDialogClose(); } : handleMobileDialogClose}
                  sx={{ 
                    padding: '0.75rem',
                    transition: 'all 0.2s ease-in-out',
                    ...((item.href || item.scrollTo) && {
                      '&:hover': {
                        backgroundColor: '#F3F4F6',
                        borderRadius: '8px',
                        transform: 'translateX(4px)',
                      },
                    }),
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: (item.href || item.scrollTo) ? 'pointer' : 'default',
                    borderRadius: '8px',
                    mb: 0.5,
                  }}
                >
                  <ListItemIcon 
                    sx={{
                      color: 'black',
                      fontSize: '1.2rem',
                      minWidth: '35px',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <Box sx={{ flex: 1 }}>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                      }}
                    />
                    {item.description && (
                      <Typography 
                        variant="body2" 
                        color="#6B7280"
                        sx={{ fontSize: '0.8rem', mt: '0.1rem', lineHeight: 1.3 }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                  {(item.href || item.scrollTo) && (
                    <ArrowForward 
                      sx={{
                        color: '#6B7280',
                        fontSize: '1rem',
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div>
      <Button
        variant="text"
        {...bindHover(popupState)}
        sx={{
          borderRadius: '10px',
          backgroundColor: popupState.isOpen ? 'black' : 'transparent',
          color: popupState.isOpen ? 'white' : 'black',
          fontSize: '0.95rem',
          fontWeight: '500',
          cursor: 'default',
          '&:hover': {
            backgroundColor: 'black',
            color: 'white',
            cursor: 'default',
          },
          padding: '0.6rem 1.2rem',
          boxShadow: 'none',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Button>
      <Popper {...bindPopper(popupState)} placement="bottom" style={{ zIndex: 1300 }}>
        <Paper
          sx={{
            mt: '0.5rem',
            borderRadius: '12px',
            backgroundColor: '#3678f3ff',
            color: 'black',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '1px solid #8ab9ffff',
            minWidth: '300px',
            maxWidth: '400px',
          }}
        >
          <Box p={2}>
            <List sx={{ p: 0 }}>
              {content.map((item, index) => (
                <ListItem 
                  key={index} 
                  component={item.href || item.scrollTo ? Link : 'div'}
                  href={item.href ? item.href : undefined}
                  target={item.href && (item.href.startsWith('/') || item.href.startsWith('#')) ? '_self' : '_blank'}
                  onClick={item.scrollTo ? () => handleScrollTo(item.scrollTo) : undefined}
                  sx={{ 
                    padding: '0.75rem',
                    transition: 'all 0.2s ease-in-out',
                    ...((item.href || item.scrollTo) && {
                      '&:hover': {
                        backgroundColor: 'rgba(82, 163, 255, 1)',
                        borderRadius: '8px',
                        border: '1px solid #ffffff',
                        transform: 'translateY(-2px)',
                      },
                    }),
                    border: '1px solid transparent',
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: '8px',
                    cursor: (item.href || item.scrollTo) ? 'pointer' : 'default',
                  }}
                >
                  <Box display="flex" alignItems="center" flex={1}>
                    <ListItemIcon 
                      sx={{
                        color: 'black',
                        fontSize: '1.3rem',
                        minWidth: '35px',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <ListItemText 
                        primary={item.text} 
                        primaryTypographyProps={{
                          variant: 'body1',
                          fontSize: '0.95rem',
                          fontWeight: '500'
                        }}
                      />
                      {item.description && (
                        <Typography 
                          variant="body2" 
                          color="#ffffffff"
                          sx={{ 
                            fontSize: 
                            '0.8rem', 
                            mt: '0.2rem', 
                            lineHeight: 1.3,
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {(item.href || item.scrollTo) && (
                    <ArrowForward 
                      sx={{
                        color: '#ffffffff',
                        fontSize: '1rem',
                        ml: 1,
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>
      </Popper>
    </div>
  );
};

const NavOptions = () => {
  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");

  if (!isNonMobileScreens) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        mt="1rem"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.5rem',
            width: '100%',
            maxWidth: '320px',
            padding: '0.5rem',
            borderRadius: '10px',
            border: '1px solid rgba(86,118,191,1)',
            backgroundColor: 'rgba(122, 177, 248, 0.2)', // Light blue background
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
        >
          {options.map((option) => (
            <DropdownOption
              key={option.label}
              option={option}
              isMobile={true}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      gap="0.5rem"
      justifyContent="center"
      alignItems="center"
      borderRadius="10px"
      border="1px solid rgba(86,118,191,1)"
      backgroundColor="rgba(122, 177, 248, 0.2)"
      color="black"
      padding="0.3rem"
      sx={{
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
      }}
    >
      {options.map((option) => (
        <DropdownOption
          key={option.label}
          option={option}
        />
      ))}
    </Box>
  );
};

export default NavOptions;